import { Vector3 } from "three";
import { levenbergMarquardt } from "ml-levenberg-marquardt";
import {
  EigenvalueDecomposition,
  Matrix,
  pseudoInverse,
  SingularValueDecomposition,
} from "ml-matrix";
import type { CylinderSurface } from "./cylinder-surface";

export const CYLINDER_FIT_CONFIG = {
  version: 1,
  minimumSamples: 50,
  normalNeighbors: 20,
  maximumNormalSamples: 1024,
  seedSpacingFactor: 10,
  maximumSeedRadius: 2,
  seedRadiusFactor: 1.5,
  seedRadiusStability: 0.02,
  seedAngleDegrees: 2,
  priorEquivalentPoints: 30,
  priorResiduals: 6,
  maximumSamples: 5000,
  outerIterations: 18,
  convergence: 1e-6,
  maximumIterations: 30,
  huberDelta: 2,
  tukeyCutoff: 4.685,
  inlierSigma: 3,
  madScale: 1.4826,
  minimumSigmaSpacing: 0.5,
  minimumConsensus: 0.6,
  angularBins: 36,
  axialBins: 10,
  minimumAngularDegrees: 45,
  depthPreviewAngularDegrees: 40,
  minimumAxialCoverage: 0.5,
  numericalFloor: 1e-10,
  solverTolerance: 1e-12,
  damping: 0.01,
  minimumRelativeRadius: 0.001,
  ransacIterations: 200,
  randomMultiplier: 1664525,
  randomIncrement: 1013904223,
  randomDivisor: 4294967296,
  eigenGapRatio: 0.2,
  minimumNormalSpread: 0.001,
  axisVarianceRatio: 2,
  minimumArcForRadius: 60,
  centerVoteRadii: 64,
  centerVoteMinimumRatio: 0.5,
  centerVoteMaximumRatio: 2,
};

/**
 * @param normals Validated local surface normals; orientation signs do not matter.
 * @returns Cylinder axis from the normal scatter eigensystem, or undefined for ambiguous support.
 */
export function cylinderAxisFromNormals(
  normals: readonly Vector3[],
): Vector3 | undefined {
  const scatter = Matrix.zeros(3, 3);
  let count = 0;
  for (const normal of normals) {
    if (
      !normal.toArray().every(Number.isFinite) ||
      normal.lengthSq() < CYLINDER_FIT_CONFIG.numericalFloor
    ) {
      continue;
    }
    const values = normal.clone().normalize().toArray();
    for (let row = 0; row < 3; row++) {
      for (let column = 0; column < 3; column++) {
        scatter.set(
          row,
          column,
          scatter.get(row, column) + values[row] * values[column],
        );
      }
    }
    count++;
  }
  if (count < 3) return;
  const decomposition = new EigenvalueDecomposition(scatter, {
    assumeSymmetric: true,
  });
  const values = decomposition.realEigenvalues;
  if (
    values[1] <= values[2] * CYLINDER_FIT_CONFIG.minimumNormalSpread ||
    values[0] > values[1] * CYLINDER_FIT_CONFIG.eigenGapRatio
  ) {
    return;
  }
  return new Vector3()
    .fromArray(decomposition.eigenvectorMatrix.getColumn(0))
    .normalize();
}

export type CylinderResidualModel = {
  residual(index: number): number;
  jacobian(index: number): number[];
  cylinder: CylinderSurface;
};

export type CylinderFitPrior = {
  cylinder: CylinderSurface;
  equivalentPoints?: number;
};

export type CylinderFitQuality = {
  inlierIndices: number[];
  rms: number;
  sigma: number;
  angularCoverage: number;
  axialCoverage: number;
  axialSpan: number;
  condition: number;
  covariance: number[][];
};

export type GeometricCylinderFit = {
  cylinder: CylinderSurface;
  quality: CylinderFitQuality;
  accepted: boolean;
  reason:
    | "accepted"
    | "insufficient-support"
    | "ill-conditioned"
    | "cancelled"
    | "invalid-input"
    | "solver-failed";
  iterations: number;
};

function median(values: number[]): number {
  if (!values.length) return 0;
  values.sort((left, right) => left - right);
  const middle = Math.floor(values.length / 2);
  return values.length % 2
    ? values[middle]
    : (values[middle - 1] + values[middle]) / 2;
}

function noiseScale(residuals: number[], spacing: number): number {
  const middle = median([...residuals]);
  return Math.max(
    CYLINDER_FIT_CONFIG.numericalFloor,
    spacing * CYLINDER_FIT_CONFIG.minimumSigmaSpacing,
    CYLINDER_FIT_CONFIG.madScale *
      median(residuals.map((residual) => Math.abs(residual - middle))),
  );
}

/**
 * @param points Observed world positions.
 * @param initial Model defining a recentered, radius-normalized five-DOF frame.
 * @param parameters Axis increments, two perpendicular translations and radius increment.
 * @param prior Optional soft current-prediction prior, never used for a seed fit.
 * @returns Signed radial residuals and their analytical five-parameter derivatives.
 */
export function cylinderResidualModel(
  points: readonly Vector3[],
  initial: CylinderSurface,
  parameters: readonly number[],
  prior?: CylinderFitPrior,
): CylinderResidualModel {
  const radial = initial.radial
    .clone()
    .projectOnPlane(initial.axis)
    .normalize();
  const tangent = new Vector3().crossVectors(initial.axis, radial).normalize();
  const direction = initial.axis
    .clone()
    .addScaledVector(radial, parameters[0])
    .addScaledVector(tangent, parameters[1]);
  const directionLength = direction.length();
  const axis = direction.normalize();
  const center = initial.center
    .clone()
    .addScaledVector(radial, initial.radius * parameters[2])
    .addScaledVector(tangent, initial.radius * parameters[3]);
  const radius = initial.radius * (1 + parameters[4]);
  const priorValues: number[] = [];
  const priorDerivatives: number[][] = [];
  if (prior) {
    const strength = Math.sqrt(
      prior.equivalentPoints ?? CYLINDER_FIT_CONFIG.priorEquivalentPoints,
    );
    const referenceAxis = prior.cylinder.axis.clone().normalize();
    const referenceRadial = prior.cylinder.radial
      .clone()
      .projectOnPlane(referenceAxis)
      .normalize();
    const referenceTangent = new Vector3().crossVectors(
      referenceAxis,
      referenceRadial,
    );
    const firstDerivative = radial
      .clone()
      .addScaledVector(axis, -axis.dot(radial))
      .divideScalar(directionLength);
    const secondDerivative = tangent
      .clone()
      .addScaledVector(axis, -axis.dot(tangent))
      .divideScalar(directionLength);
    const difference = axis
      .clone()
      .sub(referenceAxis)
      .multiplyScalar(strength)
      .toArray();
    for (let coordinate = 0; coordinate < 3; coordinate++) {
      priorValues.push(difference[coordinate]);
      priorDerivatives.push([
        firstDerivative.getComponent(coordinate) * strength,
        secondDerivative.getComponent(coordinate) * strength,
        0,
        0,
        0,
      ]);
    }
    for (const reference of [referenceRadial, referenceTangent]) {
      priorValues.push(
        (center.clone().sub(prior.cylinder.center).dot(reference) /
          initial.radius) *
          strength,
      );
      priorDerivatives.push([
        0,
        0,
        radial.dot(reference) * strength,
        tangent.dot(reference) * strength,
        0,
      ]);
    }
    priorValues.push(
      ((radius - prior.cylinder.radius) / initial.radius) * strength,
    );
    priorDerivatives.push([0, 0, 0, 0, strength]);
  }
  const offset = new Vector3();
  function components(index: number): {
    axial: number;
    distance: number;
    radial: number;
    tangent: number;
  } {
    offset.subVectors(points[index], center).divideScalar(initial.radius);
    const axial = offset.dot(axis);
    offset.addScaledVector(axis, -axial);
    const distance = offset.length();
    offset.divideScalar(Math.max(distance, CYLINDER_FIT_CONFIG.numericalFloor));
    return {
      axial,
      distance,
      radial: offset.dot(radial),
      tangent: offset.dot(tangent),
    };
  }
  return {
    cylinder: {
      center,
      axis,
      radial: radial.clone().projectOnPlane(axis).normalize(),
      radius,
    },
    residual: (index) =>
      index < points.length
        ? components(index).distance - radius / initial.radius
        : priorValues[index - points.length],
    jacobian: (index) => {
      if (index >= points.length) {
        return priorDerivatives[index - points.length];
      }
      const sample = components(index);
      return [
        (-sample.axial * sample.radial) / directionLength,
        (-sample.axial * sample.tangent) / directionLength,
        -sample.radial,
        -sample.tangent,
        -1,
      ];
    },
  };
}

/**
 * @param points Observed samples.
 * @param initial Starting model.
 * @param weights Optional square-root residual weights for robust IRLS.
 * @param prior Optional soft tracking prediction.
 * @returns One joint geometric LM solve in the normalized fit frame.
 */
export function fitCylinderGeometric(
  points: readonly Vector3[],
  initial: CylinderSurface,
  weights?: readonly number[],
  prior?: CylinderFitPrior,
): CylinderSurface {
  const count =
    points.length + (prior ? CYLINDER_FIT_CONFIG.priorResiduals : 0);
  const data = {
    x: Array.from({ length: count }, (_, index) => index),
    y: Array.from({ length: count }, () => 0),
  };
  const fit = levenbergMarquardt(
    data,
    (parameters) => {
      const model = cylinderResidualModel(points, initial, parameters, prior);
      return (index) => model.residual(index) * (weights?.[index] ?? 1);
    },
    {
      initialValues: [0, 0, 0, 0, 0],
      jacobianFunction: (parameters) => {
        const model = cylinderResidualModel(points, initial, parameters, prior);
        return (index) =>
          model.jacobian(index).map((value) => value * (weights?.[index] ?? 1));
      },
      minValues: [
        -Infinity,
        -Infinity,
        -Infinity,
        -Infinity,
        -1 + CYLINDER_FIT_CONFIG.minimumRelativeRadius,
      ],
      maxIterations: CYLINDER_FIT_CONFIG.maximumIterations,
      errorTolerance: CYLINDER_FIT_CONFIG.solverTolerance,
      damping: CYLINDER_FIT_CONFIG.damping,
    },
  );
  return cylinderResidualModel(points, initial, fit.parameterValues).cylinder;
}

/**
 * @param points Observed positions, already bounded by the data adapter.
 * @param cylinder Fitted geometric cylinder.
 * @param spacing Measured local spacing in metres.
 * @returns Noise, support and approximate five-parameter covariance in the fit frame.
 */
export function cylinderFitQuality(
  points: readonly Vector3[],
  cylinder: CylinderSurface,
  spacing: number,
): CylinderFitQuality {
  const model = cylinderResidualModel(points, cylinder, [0, 0, 0, 0, 0]);
  const residuals = points.map(
    (_, index) => model.residual(index) * cylinder.radius,
  );
  const sigma = noiseScale(residuals, spacing);
  const inlierIndices = residuals.flatMap((residual, index) =>
    Math.abs(residual) <= CYLINDER_FIT_CONFIG.inlierSigma * sigma
      ? [index]
      : [],
  );
  const tangent = new Vector3().crossVectors(cylinder.axis, cylinder.radial);
  const offset = new Vector3();
  const angles = new Set<number>();
  const axialPositions: number[] = [];
  const gram = Matrix.zeros(5, 5);
  let squaredError = 0;
  for (const index of inlierIndices) {
    offset.subVectors(points[index], cylinder.center);
    const angle = Math.atan2(offset.dot(tangent), offset.dot(cylinder.radial));
    angles.add(
      Math.floor(
        ((angle + Math.PI) / (2 * Math.PI)) * CYLINDER_FIT_CONFIG.angularBins,
      ) % CYLINDER_FIT_CONFIG.angularBins,
    );
    axialPositions.push(offset.dot(cylinder.axis));
    squaredError += residuals[index] ** 2;
    const derivative = model.jacobian(index);
    for (let row = 0; row < 5; row++) {
      for (let column = 0; column < 5; column++) {
        gram.set(
          row,
          column,
          gram.get(row, column) + derivative[row] * derivative[column],
        );
      }
    }
  }
  const minimum = axialPositions.length ? Math.min(...axialPositions) : 0;
  const maximum = axialPositions.length ? Math.max(...axialPositions) : 0;
  const span = maximum - minimum;
  const axial = new Set(
    axialPositions.map((position) =>
      Math.min(
        CYLINDER_FIT_CONFIG.axialBins - 1,
        Math.floor(
          ((position - minimum) /
            Math.max(span, CYLINDER_FIT_CONFIG.numericalFloor)) *
            CYLINDER_FIT_CONFIG.axialBins,
        ),
      ),
    ),
  );
  const decomposition = new SingularValueDecomposition(gram);
  const covariance = pseudoInverse(gram)
    .mul((sigma / cylinder.radius) ** 2)
    .to2DArray();
  const units = [1, 1, cylinder.radius, cylinder.radius, cylinder.radius];
  for (let row = 0; row < 5; row++) {
    for (let column = 0; column < 5; column++) {
      covariance[row][column] *= units[row] * units[column];
    }
  }
  return {
    inlierIndices,
    sigma,
    rms: Math.sqrt(squaredError / Math.max(1, inlierIndices.length)),
    angularCoverage: (angles.size / CYLINDER_FIT_CONFIG.angularBins) * 360,
    axialCoverage:
      span > CYLINDER_FIT_CONFIG.numericalFloor
        ? axial.size / CYLINDER_FIT_CONFIG.axialBins
        : 0,
    axialSpan: span,
    condition: decomposition.condition,
    covariance,
  };
}

/**
 * @param points Bounded observed world positions.
 * @param initial Starting cylinder; it is never mutated.
 * @param options Local spacing and cooperative cancellation.
 * @returns Joint geometric fit with noise-derived robust weights and explicit quality.
 */
export async function refineCylinderGeometric(
  points: readonly Vector3[],
  initial: CylinderSurface,
  options: {
    spacing: number;
    cancelled?(): boolean;
    prior?: CylinderFitPrior;
    depthPreview?: boolean;
  },
): Promise<GeometricCylinderFit> {
  let current = {
    ...initial,
    center: initial.center.clone(),
    axis: initial.axis.clone(),
    radial: initial.radial.clone(),
  };
  let iterations = 0;
  let cancelled = false;
  let solverFailed = false;
  const valid =
    points.length >= CYLINDER_FIT_CONFIG.minimumSamples &&
    points.length <= CYLINDER_FIT_CONFIG.maximumSamples &&
    points.every((point) => point.toArray().every(Number.isFinite)) &&
    Number.isFinite(initial.radius) &&
    initial.radius > 0 &&
    Number.isFinite(options.spacing) &&
    options.spacing > 0 &&
    initial.center.toArray().every(Number.isFinite) &&
    initial.axis.toArray().every(Number.isFinite) &&
    initial.radial.toArray().every(Number.isFinite) &&
    initial.axis.lengthSq() > 0 &&
    initial.radial.clone().cross(initial.axis).lengthSq() >
      CYLINDER_FIT_CONFIG.numericalFloor;
  const emptyQuality: CylinderFitQuality = {
    inlierIndices: [],
    rms: Infinity,
    sigma: 0,
    angularCoverage: 0,
    axialCoverage: 0,
    axialSpan: 0,
    condition: Infinity,
    covariance: [],
  };
  if (!valid) {
    return {
      cylinder: current,
      quality: emptyQuality,
      accepted: false,
      reason: "invalid-input",
      iterations,
    };
  }
  current.axis.normalize();
  current.radial.projectOnPlane(current.axis).normalize();
  if (!options.prior && !options.cancelled?.()) {
    const centroid = points
      .reduce((sum, point) => sum.add(point), new Vector3())
      .divideScalar(points.length);
    const scatter = Matrix.zeros(3, 3);
    for (const point of points) {
      const values = point.clone().sub(centroid).toArray();
      for (let row = 0; row < 3; row++) {
        for (let column = 0; column < 3; column++) {
          scatter.set(
            row,
            column,
            scatter.get(row, column) + values[row] * values[column],
          );
        }
      }
    }
    const decomposition = new EigenvalueDecomposition(scatter, {
      assumeSymmetric: true,
    });
    const values = decomposition.realEigenvalues;
    if (values[2] > values[1] * CYLINDER_FIT_CONFIG.axisVarianceRatio) {
      const axis = new Vector3()
        .fromArray(decomposition.eigenvectorMatrix.getColumn(2))
        .normalize();
      if (axis.dot(current.axis) < 0) axis.negate();
      const radial = current.radial.clone().projectOnPlane(axis).normalize();
      const candidate = { ...current, axis, radial };
      const baseline = cylinderResidualModel(points, current, [0, 0, 0, 0, 0]);
      const alternative = cylinderResidualModel(
        points,
        candidate,
        [0, 0, 0, 0, 0],
      );
      const baselineError = median(
        points.map((_, index) => Math.abs(baseline.residual(index))),
      );
      const alternativeError = median(
        points.map((_, index) => Math.abs(alternative.residual(index))),
      );
      if (
        alternativeError < baselineError &&
        radial.lengthSq() > CYLINDER_FIT_CONFIG.minimumNormalSpread
      ) {
        current = candidate;
      }
    }
  }
  for (; iterations < CYLINDER_FIT_CONFIG.outerIterations; iterations++) {
    if (options.cancelled?.()) {
      cancelled = true;
      break;
    }
    const previous = current;
    const model = cylinderResidualModel(points, current, [0, 0, 0, 0, 0]);
    const initialResiduals = points.map(
      (_, index) => model.residual(index) * current.radius,
    );
    const radiusOffset = median([...initialResiduals]);
    const sigma = noiseScale(initialResiduals, options.spacing);
    if (
      Math.abs(radiusOffset) > CYLINDER_FIT_CONFIG.tukeyCutoff * sigma &&
      current.radius + radiusOffset > CYLINDER_FIT_CONFIG.numericalFloor
    ) {
      current = { ...current, radius: current.radius + radiusOffset };
    }
    const residuals = initialResiduals.map(
      (residual) => residual - (current.radius - previous.radius),
    );
    const weights = residuals.map((residual) => {
      const normalized =
        Math.abs(residual) / (CYLINDER_FIT_CONFIG.tukeyCutoff * sigma);
      return normalized < 1 ? 1 - normalized * normalized : 0;
    });
    try {
      current = fitCylinderGeometric(points, current, weights, options.prior);
    } catch {
      solverFailed = true;
      break;
    }
    if (
      !(current.radius > 0) ||
      ![
        ...current.center.toArray(),
        ...current.axis.toArray(),
        current.radius,
      ].every(Number.isFinite)
    ) {
      current = previous;
      solverFailed = true;
      break;
    }
    const accepted = cylinderFitQuality(
      points,
      current,
      options.spacing,
    ).inlierIndices;
    if (accepted.length) {
      const centroid = accepted
        .reduce((sum, index) => sum.add(points[index]), new Vector3())
        .divideScalar(accepted.length);
      current.center.addScaledVector(
        current.axis,
        centroid.sub(current.center).dot(current.axis),
      );
    }
    const change =
      Math.abs(current.radius / previous.radius - 1) +
      current.axis.distanceTo(previous.axis) +
      current.center
        .clone()
        .sub(previous.center)
        .cross(previous.axis)
        .length() /
        previous.radius;
    if (iterations > 1 && change < CYLINDER_FIT_CONFIG.convergence) {
      iterations++;
      break;
    }
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }
  cancelled ||= options.cancelled?.() ?? false;
  const quality = cylinderFitQuality(
    points,
    current,
    Math.max(options.spacing || 0, CYLINDER_FIT_CONFIG.numericalFloor),
  );
  const supported =
    quality.inlierIndices.length >= CYLINDER_FIT_CONFIG.minimumSamples &&
    quality.inlierIndices.length >=
      points.length * CYLINDER_FIT_CONFIG.minimumConsensus &&
    quality.angularCoverage >=
      (options.depthPreview
        ? CYLINDER_FIT_CONFIG.depthPreviewAngularDegrees
        : CYLINDER_FIT_CONFIG.minimumAngularDegrees) &&
    quality.axialCoverage >= CYLINDER_FIT_CONFIG.minimumAxialCoverage;
  const conditioned =
    Number.isFinite(quality.condition) &&
    quality.condition < 1 / CYLINDER_FIT_CONFIG.solverTolerance;
  let reason: GeometricCylinderFit["reason"] = "accepted";
  if (!supported) reason = "insufficient-support";
  if (!conditioned) reason = "ill-conditioned";
  if (solverFailed) reason = "solver-failed";
  if (cancelled) reason = "cancelled";
  return {
    cylinder: current,
    quality,
    accepted: reason === "accepted",
    reason,
    iterations,
  };
}
